'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';

type IconProps = { size?: number };

/** Brand icons as local SVGs — Lucide brand icons are deprecated (removed in v1). */
function FacebookIcon({ size = 20 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

function InstagramIcon({ size = 20 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

function LinkedInIcon({ size = 20 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

function TikTokIcon({ size = 20 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.73a8.19 8.19 0 0 0 4.76 1.52V6.8a4.84 4.84 0 0 1-1-.11z" />
    </svg>
  );
}

export function Footer() {
  const t = useTranslations('footer');

  return (
    <footer className="bg-gradient-to-b from-primary to-very-dark-navy border-t border-very-light-turquoise/25">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">{t('links')}</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/" className="text-white/90 hover:text-very-light-turquoise transition-colors">
                  {t('home')}
                </Link>
              </li>
              <li>
                <Link href="/safetizen" className="text-white/90 hover:text-very-light-turquoise transition-colors">
                  {t('safetizen')}
                </Link>
              </li>
              <li>
                <Link href="/earit" className="text-white/90 hover:text-very-light-turquoise transition-colors">
                  {t('earit')}
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-white/90 hover:text-very-light-turquoise transition-colors">
                  {t('contact')}
                </Link>
              </li>
              <li>
                <Link href="/a-propos" className="text-white/90 hover:text-very-light-turquoise transition-colors">
                  {t('about')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white mb-4">{t('legal')}</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/mentions-legales" className="text-white/90 hover:text-very-light-turquoise transition-colors">
                  {t('mentionsLegales')}
                </Link>
              </li>
              <li>
                <Link href="/politique-confidentialite" className="text-white/90 hover:text-very-light-turquoise transition-colors">
                  {t('politiqueConfidentialite')}
                </Link>
              </li>
              <li>
                <Link href="/cgu" className="text-white/90 hover:text-very-light-turquoise transition-colors">
                  {t('cgu')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white mb-4">{t('social')}</h3>
            <div className="flex gap-4">
              <a
                href="#"
                aria-label="Facebook"
                className="text-white/90 hover:text-very-light-turquoise transition-colors"
              >
                <FacebookIcon size={20} />
              </a>
              <a
                href="#"
                aria-label="Instagram"
                className="text-white/90 hover:text-very-light-turquoise transition-colors"
              >
                <InstagramIcon size={20} />
              </a>
              <a
                href="#"
                aria-label="LinkedIn"
                className="text-white/90 hover:text-very-light-turquoise transition-colors"
              >
                <LinkedInIcon size={20} />
              </a>
              <a
                href="#"
                aria-label="TikTok"
                className="text-white/90 hover:text-very-light-turquoise transition-colors"
              >
                <TikTokIcon size={20} />
              </a>
            </div>
          </div>

          <div>
            <p className="text-white/90 text-sm">{t('copyright')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
